import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import SignaturePad from "react-signature-canvas";
import { AccordionSection } from "./AccordionSection";
import { SectionErrors } from "./SectionErrors";
import { SignedImage } from "../../common/SignedImage";

export function FinanceSatisfactionNoteSection({ jobToComplete, financeNoteHandler, setError, errorMessage }) {
  const today = new Date();
  const saved = jobToComplete.customerSatNote || {};

  const [warrantyProvided, setWarrantyProvided] = useState(() => {
    if (saved.warrantyDocProvided === true) return "Y";
    if (saved.warrantyDocProvided === false) return "N";
    return null;
  });
  const [descriptionOfWork] = useState(() => {
    if (saved.workDescription) return saved.workDescription;
    const jd = jobToComplete.jobDetail || {};
    const boiler = jobToComplete.boiler || {};
    const mainLines = [
      `Supply and fit of new ${boiler.make || ""} ${boiler.model || ""} boiler, including required pipework, flue kit, cleanse and thermostat`.trim(),
    ];
    if (jd.convertToCombi === true) mainLines.push("Convert current boiler to a Combi boiler");
    if (jd.boilerMove === true) mainLines.push(`Move boiler from ${jd.currentBoilerLocation} to ${jd.newBoilerLocation}`);
    if (jd.newFlueExit) mainLines.push(`Move flue exit to ${jd.newFlueExit}`);
    const addons = jobToComplete.productAddOns || jobToComplete.productAddons || [];
    const parts = [mainLines.join("\n")];
    if (addons.length > 0) {
      const addonLines = ["", "Installation of additional items:"];
      addons.forEach((a) => addonLines.push(`• ${a.name}${a.qty > 1 ? ` (x${a.qty})` : ""}`));
      parts.push(addonLines.join("\n"));
    }
    return parts.join("\n");
  });
  const orderTotal = Number(jobToComplete.orderTotal || 0);
  const [amountOfCredit, setAmountOfCredit] = useState(
    saved.creditAmount != null ? String(saved.creditAmount) : String(orderTotal)
  );
  const [customerName, setCustomerName] = useState(
    saved.customerFirstName || `${jobToComplete.name.firstName} ${jobToComplete.name.lastName}`
  );
  const address = jobToComplete.address;
  const [address1, setAddress1] = useState(saved.customerAddress1 || address.address1 || "");
  const [address2, setAddress2] = useState(saved.customerAddress2 || address.address2 || "");
  const [address3, setAddress3] = useState(saved.customerAddress3 || address.address3 || "");
  const [city, setCity] = useState(saved.customerCity || address.city || "");
  const [county, setCounty] = useState(saved.customerCounty || address.county || "");
  const [postcode, setPostcode] = useState(saved.customerPostcode || address.postcode || "");
  const [telephoneNumber, setTelephoneNumber] = useState(saved.telephoneNumber || jobToComplete.mobileNumber || "");
  const sigCanvas = useRef({});

  const isComplete = !!(jobToComplete.customerSatNote?.customerSignature);

  const clearCanvas = () => sigCanvas.current.clear();

  const sanitiseName = (val) => val.replace(/[^a-zA-Z\s'\-.]/g, "");
  const sanitiseAddress = (val) => val.replace(/[^a-zA-Z0-9\s'\-.,/]/g, "");

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/[^\d\s+\-()]/g, "");
    setTelephoneNumber(raw);
  };

  const isValidPhone = (val) => /^(\+44\s?|0)[1-9](\s?\d){8,9}$/.test(val.replace(/[\s\-()]/g, "").replace(/^\+44/, "0"));

  const handleAmountChange = (e) => {
    const raw = e.target.value;
    if (raw === "" || raw === ".") {
      setAmountOfCredit(raw);
      return;
    }
    if (/^\d*\.?\d{0,2}$/.test(raw)) {
      const val = Number(raw);
      if (!isNaN(val) && val <= orderTotal) setAmountOfCredit(raw);
    }
  };

  const saveCanvas = () => {
    if (!warrantyProvided) {
      setError("financeSatisfactionNote", "noWarranty", "Please indicate whether guarantee and warranty documentation has been provided");
      return;
    }
    if (!descriptionOfWork.trim()) {
      setError("financeSatisfactionNote", "noDescription", "Please enter a description of work");
      return;
    }
    const parsedAmount = Number(amountOfCredit);
    if (!amountOfCredit || isNaN(parsedAmount) || parsedAmount <= 0 || parsedAmount > orderTotal) {
      setError("financeSatisfactionNote", "invalidAmount", `Amount of credit must be between £0.01 and £${orderTotal.toFixed(2)}`);
      return;
    }
    if (!isValidPhone(telephoneNumber)) {
      setError("financeSatisfactionNote", "invalidPhone", "Please enter a valid UK phone number");
      return;
    }
    if (sigCanvas.current.isEmpty()) {
      setError("financeSatisfactionNote", "noSignature", "Please obtain the customer's signature before submitting");
      return;
    }
    financeNoteHandler({
      signatureUrl: sigCanvas.current.getCanvas().toDataURL("image/png"),
      warrantyProvided,
      descriptionOfWork,
      amountOfCredit: parsedAmount,
      customerName,
      deliveryAddress: { address1, address2, address3, city, county, postcode },
      telephoneNumber,
    });
  };

  return (
    <AccordionSection
      id="financeSatisfactionNote"
      headerId="headerFinanceSatisfactionNote"
      collapseId="collapseFinanceSatisfactionNote"
      number={7}
      title="Finance Satisfaction Note"
      isComplete={isComplete}
      startOpen={false}
    >
      <h6 className="text-center fw-bold border-bottom pb-2 mb-3">
        Completion Note &amp; Authority to Pay &ndash; Work Completed
      </h6>

      <div className="form-group mb-3">
        <label className="text-muted">Sales Order No</label>
        <input className="form-control" type="text" readOnly value={jobToComplete.orderNumber} />
      </div>

      <div className="form-group mb-3">
        <label className="text-muted">Supplier Name</label>
        <input className="form-control" type="text" readOnly value="BoxHeat Ltd" />
      </div>

      <div className="form-group mb-3">
        <label className="text-muted">Description of work</label>
        <textarea
          className="form-control"
          rows={5}
          readOnly
          value={descriptionOfWork}
        />
      </div>

      <div className="form-group mb-3">
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-between gap-2">
          <label className="text-muted mb-0">
            Has applicable guarantee and warranty documentation been provided?
          </label>
          <div className="d-flex justify-content-end gap-3">
          <button
            type="button"
            className={`btn ${warrantyProvided === "Y" ? "btn-success" : "btn-outline-secondary"}`}
            disabled={isComplete}
            onClick={() => setWarrantyProvided("Y")}
          >
            Yes
          </button>
          <button
            type="button"
            className={`btn ${warrantyProvided === "N" ? "btn-danger" : "btn-outline-secondary"}`}
            disabled={isComplete}
            onClick={() => setWarrantyProvided("N")}
          >
            No
          </button>
          </div>
        </div>
      </div>

      <div className="form-group mb-3">
        <label className="text-muted">Amount of Credit (max £{orderTotal.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</label>
        <div className="input-group">
          <span className="input-group-text">£</span>
          <input
            className="form-control"
            type="number"
            min="0.01"
            max={orderTotal}
            step="0.01"
            disabled={isComplete}
            value={amountOfCredit}
            onChange={handleAmountChange}
          />
        </div>
      </div>

      <div className="form-group mb-3">
        <label className="text-muted">Customer Name</label>
        <input
          className="form-control"
          type="text"
          disabled={isComplete}
          value={customerName}
          onChange={(e) => setCustomerName(sanitiseName(e.target.value))}
        />
      </div>

      <div className="form-group mb-3">
        <label className="text-muted">Delivery Address (must match the application address)</label>
        <input className="form-control mb-1" type="text" placeholder="Address line 1" disabled={isComplete} value={address1} onChange={(e) => setAddress1(sanitiseAddress(e.target.value))} />
        <input className="form-control mb-1" type="text" placeholder="Address line 2" disabled={isComplete} value={address2} onChange={(e) => setAddress2(sanitiseAddress(e.target.value))} />
        <input className="form-control mb-1" type="text" placeholder="Address line 3" disabled={isComplete} value={address3} onChange={(e) => setAddress3(sanitiseAddress(e.target.value))} />
        <input className="form-control mb-1" type="text" placeholder="City" disabled={isComplete} value={city} onChange={(e) => setCity(sanitiseAddress(e.target.value))} />
        <input className="form-control mb-1" type="text" placeholder="County" disabled={isComplete} value={county} onChange={(e) => setCounty(sanitiseAddress(e.target.value))} />
        <input className="form-control" type="text" placeholder="Postcode" disabled={isComplete} value={postcode} onChange={(e) => setPostcode(sanitiseAddress(e.target.value))} />
      </div>

      <div className="form-group mb-3">
        <label className="text-muted">Telephone Number</label>
        <input
          className="form-control"
          type="text"
          disabled={isComplete}
          value={telephoneNumber}
          onChange={handlePhoneChange}
        />
      </div>

      <p className="small text-muted border rounded p-2 mb-3">
        I hereby confirm that the work listed, has been completed by the named supplier, at the above address and I
        hereby authorise you to pay the supplier, on my behalf, the amount of credit for the work specified in the
        credit agreement between us.<br /><br />
        I am aware that I will also receive a call from Ideal4finance to verify this document.
      </p>

      <div className="form-group mb-3">
        <label className="text-muted">Signature of Customer</label>
        {!isComplete ? (
          <SignaturePad ref={sigCanvas} canvasProps={{ className: "signatureCanvas" }} />
        ) : saved.customerSignature?.url ? (
          <SignedImage
            src={saved.customerSignature.url}
            className="signatureCanvas mb-2"
          />
        ) : null}
      </div>

      <div className="form-group mb-3">
        <label className="text-muted">Date of Signature</label>
        <input
          className="form-control"
          type="text"
          readOnly
          value={saved.customerSignatureDatetime
            ? new Date(saved.customerSignatureDatetime).toLocaleDateString("en-GB")
            : today.toLocaleDateString("en-GB")}
        />
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-md-end gap-2 mt-2">
        <button className="btn btn-danger" onClick={clearCanvas} disabled={isComplete}>
          Clear Signature
        </button>
        <button className="btn btn-primary" onClick={saveCanvas} disabled={isComplete}>
          Submit Finance Satisfaction Note
        </button>
      </div>
      <SectionErrors errorMessage={errorMessage} section="financeSatisfactionNote" />
    </AccordionSection>
  );
}

FinanceSatisfactionNoteSection.propTypes = {
  jobToComplete: PropTypes.object.isRequired,
  financeNoteHandler: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  errorMessage: PropTypes.array.isRequired,
};
